import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Select } from '../components/Select';
import { InboxIcon, FileIcon, BookIcon, MessageSquareIcon, MailIcon, BellIcon, SettingsIcon } from '../components/Icons';

interface Notification {
  id: number;
  type: 'query_response' | 'beta_application' | 'manuscript_request' | 'feedback_received' | 'message' | 'system';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  actionLink?: string;
  actionLabel?: string;
}

export const NotificationsCenterPage = () => {
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: 1,
      type: 'query_response',
      title: 'Query Response from Sarah Johnson',
      message: 'Sarah Johnson has requested your full manuscript for "The Silent Shore". Click to view details.',
      timestamp: '2024-03-15T10:30:00Z',
      read: false,
      actionLink: '/writer/queries/1',
      actionLabel: 'View Query',
    },
    {
      id: 2,
      type: 'beta_application',
      title: 'New Beta Reader Application',
      message: 'Emma Rodriguez has applied to beta read your manuscript "Echoes of Tomorrow". Review the application now.',
      timestamp: '2024-03-15T09:15:00Z',
      read: false,
      actionLink: '/writer/manuscripts/1/applications',
      actionLabel: 'Review Application',
    },
    {
      id: 3,
      type: 'feedback_received',
      title: 'Beta Reader Feedback Received',
      message: 'David Park has submitted feedback for Chapter 5 of "The Silent Shore". View their comments and suggestions.',
      timestamp: '2024-03-14T16:45:00Z',
      read: false,
      actionLink: '/writer/manuscripts/1/feedback',
      actionLabel: 'View Feedback',
    },
    {
      id: 4,
      type: 'message',
      title: 'New Message from Michael Chen',
      message: 'Michael Chen sent you a message regarding "Beta Reading Exchange". Click to read and respond.',
      timestamp: '2024-03-14T14:20:00Z',
      read: true,
      actionLink: '/messages/3',
      actionLabel: 'View Message',
    },
    {
      id: 5,
      type: 'manuscript_request',
      title: 'Manuscript Request Approved',
      message: 'Your request to read "The Whispering Woods" has been approved. You can now access the full manuscript.',
      timestamp: '2024-03-13T11:00:00Z',
      read: true,
      actionLink: '/reader/manuscripts/3/read',
      actionLabel: 'Start Reading',
    },
    {
      id: 6,
      type: 'system',
      title: 'Profile Completion Reminder',
      message: 'Your profile is 75% complete. Add your author bio and writing credentials to improve your visibility.',
      timestamp: '2024-03-12T08:00:00Z',
      read: true,
      actionLink: '/writer/profile/edit',
      actionLabel: 'Complete Profile',
    },
    {
      id: 7,
      type: 'beta_application',
      title: 'Beta Reader Application Accepted',
      message: 'Congratulations! Your application to beta read "Shadows in the Mist" by Jennifer Williams has been accepted.',
      timestamp: '2024-03-11T15:30:00Z',
      read: true,
      actionLink: '/reader/manuscripts',
      actionLabel: 'View Manuscript',
    },
    {
      id: 8,
      type: 'query_response',
      title: 'Query Update',
      message: 'Your query to Jennifer Williams has been marked as "Under Review". Response expected within 6-8 weeks.',
      timestamp: '2024-03-10T10:00:00Z',
      read: true,
      actionLink: '/writer/queries/2',
      actionLabel: 'View Query',
    },
  ]);

  const getNotificationIcon = (type: string): React.ReactNode => {
    switch (type) {
      case 'query_response':
        return <InboxIcon width={24} height={24} />;
      case 'beta_application':
        return <FileIcon width={24} height={24} />;
      case 'manuscript_request':
        return <BookIcon width={24} height={24} />;
      case 'feedback_received':
        return <MessageSquareIcon width={24} height={24} />;
      case 'message':
        return <MailIcon width={24} height={24} />;
      case 'system':
        return <SettingsIcon width={24} height={24} />;
      default:
        return <BellIcon width={24} height={24} />;
    }
  };

  const getNotificationTypeLabel = (type: string): string => {
    switch (type) {
      case 'query_response':
        return 'Query Response';
      case 'beta_application':
        return 'Beta Application';
      case 'manuscript_request':
        return 'Manuscript Request';
      case 'feedback_received':
        return 'Feedback Received';
      case 'message':
        return 'Message';
      case 'system':
        return 'System';
      default:
        return 'Notification';
    }
  };

  const getNotificationTypeBadgeVariant = (type: string): 'default' | 'success' | 'warning' | 'error' | 'primary' => {
    switch (type) {
      case 'query_response':
        return 'success';
      case 'beta_application':
        return 'primary';
      case 'manuscript_request':
        return 'warning';
      case 'feedback_received':
        return 'success';
      case 'message':
        return 'primary';
      case 'system':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} hours ago`;
    } else if (diffInHours < 48) {
      return '1 day ago';
    } else if (diffInHours < 168) {
      return `${Math.floor(diffInHours / 24)} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  const handleMarkAsRead = (id: number) => {
    setNotifications(notifications.map(n =>
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const handleMarkAllAsRead = () => {
    if (window.confirm('Mark all notifications as read?')) {
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      window.alert('All notifications marked as read');
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all notifications? This action cannot be undone.')) {
      setNotifications([]);
      window.alert('All notifications cleared');
    }
  };

  const handleClearRead = () => {
    if (window.confirm('Clear all read notifications?')) {
      setNotifications(notifications.filter(n => !n.read));
      window.alert('Read notifications cleared');
    }
  };

  // Filter notifications
  const filteredNotifications = notifications.filter(notification => {
    const matchesType = filterType === 'all' || notification.type === filterType;
    const matchesStatus = filterStatus === 'all' ||
                         (filterStatus === 'unread' && !notification.read) ||
                         (filterStatus === 'read' && notification.read);
    return matchesType && matchesStatus;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '32px',
        }}>
          <div>
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              marginBottom: '8px',
              color: theme.colors.textPrimary,
            }}>
              Notifications
            </h1>
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
            }}>
              {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}` : 'All caught up!'}
            </p>
          </div>

          <Button onClick={() => window.location.href = '/settings/notifications'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <SettingsIcon width={16} height={16} />
              <span>Settings</span>
            </div>
          </Button>
        </div>

        {/* Filters and Actions */}
        <Card style={{ marginBottom: '24px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginBottom: '16px',
          }}>
            <Select
              label="Filter by Type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'query_response', label: 'Query Responses' },
                { value: 'beta_application', label: 'Beta Applications' },
                { value: 'manuscript_request', label: 'Manuscript Requests' },
                { value: 'feedback_received', label: 'Feedback Received' },
                { value: 'message', label: 'Messages' },
                { value: 'system', label: 'System Notifications' },
              ]}
            />

            <Select
              label="Filter by Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              options={[
                { value: 'all', label: 'All Notifications' },
                { value: 'unread', label: 'Unread Only' },
                { value: 'read', label: 'Read Only' },
              ]}
            />
          </div>

          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
          }}>
            <Button
              variant="outline"
              size="small"
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0}
            >
              Mark All as Read
            </Button>
            <Button
              variant="outline"
              size="small"
              onClick={handleClearRead}
              disabled={notifications.filter(n => n.read).length === 0}
            >
              Clear Read
            </Button>
            <Button
              variant="ghost"
              size="small"
              onClick={handleClearAll}
              disabled={notifications.length === 0}
            >
              Clear All
            </Button>
          </div>
        </Card>

        {/* Notifications List */}
        {filteredNotifications.length === 0 ? (
          <Card style={{ textAlign: 'center', padding: '60px 40px' }}>
            <div style={{ marginBottom: '16px', color: theme.colors.textSecondary }}>
              <BellIcon width={48} height={48} />
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              marginBottom: '8px',
            }}>
              {notifications.length === 0 ? 'No notifications yet' : 'No notifications found'}
            </h3>
            <p style={{
              fontSize: '16px',
              color: theme.colors.textSecondary,
              marginBottom: '20px',
            }}>
              {notifications.length === 0
                ? 'When you receive notifications, they will appear here'
                : 'Try adjusting your filters'}
            </p>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {filteredNotifications.map((notification) => (
              <Card
                key={notification.id}
                style={{
                  backgroundColor: notification.read ? theme.colors.white : theme.colors.primaryLight,
                  border: notification.read ? `1px solid ${theme.colors.border}` : `2px solid ${theme.colors.primary}`,
                  position: 'relative',
                }}
              >
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start',
                }}>
                  {/* Icon */}
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: theme.colors.backgroundAlt,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    position: 'relative',
                  }}>
                    {getNotificationIcon(notification.type)}
                    {!notification.read && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        backgroundColor: theme.colors.primary,
                        border: `2px solid ${theme.colors.white}`,
                      }} />
                    )}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          marginBottom: '4px',
                          flexWrap: 'wrap',
                        }}>
                          <h3 style={{
                            fontSize: '16px',
                            fontWeight: notification.read ? '500' : '600',
                            color: theme.colors.textPrimary,
                            margin: 0,
                          }}>
                            {notification.title}
                          </h3>
                          <Badge variant={getNotificationTypeBadgeVariant(notification.type)}>
                            {getNotificationTypeLabel(notification.type)}
                          </Badge>
                        </div>
                      </div>

                      <div style={{
                        fontSize: '13px',
                        color: theme.colors.textTertiary,
                        flexShrink: 0,
                        marginLeft: '16px',
                      }}>
                        {formatTimestamp(notification.timestamp)}
                      </div>
                    </div>

                    <p style={{
                      fontSize: '14px',
                      color: theme.colors.textSecondary,
                      margin: '0 0 12px 0',
                      lineHeight: '1.5',
                    }}>
                      {notification.message}
                    </p>

                    {/* Actions */}
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                    }}>
                      {notification.actionLink && (
                        <Button
                          variant="outline"
                          size="small"
                          onClick={() => window.location.href = notification.actionLink!}
                        >
                          {notification.actionLabel || 'View Details'}
                        </Button>
                      )}
                      {!notification.read && (
                        <Button
                          variant="ghost"
                          size="small"
                          onClick={() => handleMarkAsRead(notification.id)}
                        >
                          Mark as Read
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};
